import { Tags, Team } from '@clearkit/icons';
import classnames from 'classnames';
import { useMemo } from 'react';

import { Search } from '../hooks/useTargetMarket';
import { FilterMultiPicker } from './FilterMultiPicker';

// @todo: query companies
const COUNTRIES = [
  {
    label: 'Australia',
    value: 'AU',
  },
  {
    label: 'Canada',
    value: 'CA',
  },
  {
    label: 'Ireland',
    value: 'IE',
  },
  {
    label: 'Lithuania',
    value: 'LT',
  },
  {
    label: 'Mexico',
    value: 'MX',
  },
  {
    label: 'United States',
    value: 'US',
  },
];

// @todo: query employee ranges
const EMPLOYEE_RANGES = [
  {
    label: '1-10',
    value: '1-10',
  },
  {
    label: '11-50',
    value: '11-50',
  },
  {
    label: '51-250',
    value: '51-250',
  },
  {
    label: '251-1K',
    value: '251-1K',
  },
];

// @todo: query industries
const INDUSTRIES = [
  {
    label: 'Banks',
    value: 'Banks',
  },
  {
    label: 'Consumer Staples',
    value: 'Consumer Staples',
  },
  {
    label: 'IT Services',
    value: 'IT Services',
  },
  {
    label: 'Internet Software & Services',
    value: 'Internet Software & Services',
  },
];

// Combined with COMPANY_TYPES to query tags
const BUSINESS_MODELS = [
  {
    label: 'B2B',
    value: 'B2B',
  },
  {
    label: 'B2C',
    value: 'B2C',
  },
];

// Combined with BUSINESS_MODELS to query tags
const COMPANY_TYPES = [
  {
    label: 'Education',
    value: 'education',
  },
  {
    label: 'Government',
    value: 'government',
  },
  {
    label: 'Non-Profit',
    value: 'nonprofit',
  },
  {
    label: 'Private',
    value: 'private',
  },
  {
    label: 'Public',
    value: 'public',
  },
];

// @todo: query ranges
const RAISED_RANGES = [
  {
    label: '0-1M',
    value: '0-1M',
  },
  {
    label: '1M-10M',
    value: '1M-10M',
  },
  {
    label: '10M-50M',
    value: '10M-50M',
  },
  {
    label: '50M-100M',
    value: '50M-100M',
  },
  {
    label: '100M+',
    value: '100M+',
  },
];

// @todo: query tags
const TECH = [
  {
    label: 'Mode Analytics',
    value: 'mode',
  },
  {
    label: 'AdRoll',
    value: 'adroll',
  },
];

export const TargetMarketForm = ({
  search,
  setSearch,
}: {
  search: Search;
  setSearch: any;
}) => {
  const { businessModel, companyType } = useMemo(() => {
    const businessModel: string[] = [];
    const companyType: string[] = [];

    search?.tag?.forEach((tag) => {
      if (BUSINESS_MODELS.find((model) => model.value === tag)) {
        businessModel.push(tag);
      } else if (COMPANY_TYPES.find((type) => type.value === tag)) {
        companyType.push(tag);
      }
    });

    return { businessModel, companyType };
  }, [search]);

  const updateSearch = (
    fieldName: keyof Search | 'businessModel' | 'companyType',
  ) => (value: string[]) => {
    let searchArg = fieldName;

    if (fieldName === 'businessModel') {
      searchArg = 'tag';
      value = [...companyType, ...value];
    }

    if (fieldName === 'companyType') {
      searchArg = 'tag';
      value = [...businessModel, ...value];
    }

    const newSearch = {
      ...search,
      [searchArg]: value,
    };

    setSearch(newSearch);
  };

  return (
    <div className="px-6 pb-6 space-y-8">
      <div className={classnames('divide-y space-y-4 divide-gray-200')}>
        <FilterMultiPicker
          className="pt-4"
          items={BUSINESS_MODELS}
          onChange={updateSearch('businessModel')}
          value={businessModel}
        >
          <FilterMultiPicker.Icon>
            <Tags />
          </FilterMultiPicker.Icon>
          <FilterMultiPicker.Title>Business Model</FilterMultiPicker.Title>
        </FilterMultiPicker>
        <FilterMultiPicker
          className="pt-4"
          items={COUNTRIES}
          onChange={updateSearch('country')}
          value={search?.country ?? []}
        >
          <FilterMultiPicker.Icon>
            <Tags />
          </FilterMultiPicker.Icon>
          <FilterMultiPicker.Title>Countries</FilterMultiPicker.Title>
        </FilterMultiPicker>
        <FilterMultiPicker
          className="pt-4"
          onChange={updateSearch('description')}
          value={search?.description ?? []}
        >
          <FilterMultiPicker.Icon>
            <Tags />
          </FilterMultiPicker.Icon>
          <FilterMultiPicker.Title>Keywords</FilterMultiPicker.Title>
        </FilterMultiPicker>
        <FilterMultiPicker
          className="pt-4"
          items={EMPLOYEE_RANGES}
          onChange={updateSearch('employees')}
          value={search?.employees ?? []}
        >
          <FilterMultiPicker.Icon>
            <Team />
          </FilterMultiPicker.Icon>
          <FilterMultiPicker.Title>Employee Range</FilterMultiPicker.Title>
        </FilterMultiPicker>
        <FilterMultiPicker
          className="pt-4"
          items={INDUSTRIES}
          onChange={updateSearch('industry')}
          value={search?.industry ?? []}
        >
          <FilterMultiPicker.Icon>
            <Tags />
          </FilterMultiPicker.Icon>
          <FilterMultiPicker.Title>Industries</FilterMultiPicker.Title>
        </FilterMultiPicker>
        <FilterMultiPicker
          className="pt-4"
          items={RAISED_RANGES}
          onChange={updateSearch('raised')}
          value={search?.raised ?? []}
        >
          <FilterMultiPicker.Icon>
            <Team />
          </FilterMultiPicker.Icon>
          <FilterMultiPicker.Title>Funding Raised</FilterMultiPicker.Title>
        </FilterMultiPicker>
        <FilterMultiPicker
          className="pt-4"
          items={TECH}
          onChange={updateSearch('tech')}
          value={search?.tech ?? []}
        >
          <FilterMultiPicker.Icon>
            <Tags />
          </FilterMultiPicker.Icon>
          <FilterMultiPicker.Title>Technology</FilterMultiPicker.Title>
        </FilterMultiPicker>
        <FilterMultiPicker
          className="pt-4"
          items={COMPANY_TYPES}
          onChange={updateSearch('companyType')}
          value={companyType ?? []}
        >
          <FilterMultiPicker.Icon>
            <Tags />
          </FilterMultiPicker.Icon>
          <FilterMultiPicker.Title>Company Types</FilterMultiPicker.Title>
        </FilterMultiPicker>
      </div>
    </div>
  );
};
